import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import client from "../api/client";

const get = (obj, pascal, camel) => obj?.[pascal] ?? obj?.[camel] ?? "";
const num = (v) => Number(v || 0).toFixed(2);

export default function StockLedgerPage() {
  const companyId = Number(localStorage.getItem("companyId") || 0);

  const [params] = useSearchParams();
  const urlItemId = params.get("itemId");

  const [items, setItems] = useState([]);
  const [itemId, setItemId] = useState(urlItemId || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadItems = async () => {
    try {
      const res = await client.get("/inventory/items", {
        params: companyId > 0 ? { companyId } : {},
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      alert(e?.response?.data?.message || "Failed to load items");
    }
  };

  const load = async (id = itemId) => {
    if (!id) {
      alert("Please select item");
      return;
    }

    try {
      setLoading(true);

      const res = await client.get(`/inventory/stock-ledger/${id}`, {
        params: companyId > 0 ? { companyId } : {},
      });

      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data ||
          e.message ||
          "Failed to load stock ledger",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();

    if (urlItemId) {
      setItemId(urlItemId);
      load(urlItemId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlItemId]);

  const selectedItem = items.find(
    (x) => String(get(x, "Id", "id")) === String(itemId),
  );

  return (
    <div className="container-fluid py-2" style={{ fontSize: 11, padding: 0 }}>
      <div
        style={{
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 2px 10px rgba(0,0,0,.08)",
          background: "#fff",
        }}
      >
        <div
          style={{
            background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
            color: "#fff",
            padding: "8px 12px",
          }}
        >
          <h6 style={{ fontSize: 13, fontWeight: 700, margin: 0 }}>
            <i className="bi bi-archive me-2"></i>
            Stock Ledger
          </h6>
          <div style={{ fontSize: 10, opacity: 0.85 }}>
            Track item-wise stock movement with running quantity
          </div>
        </div>

        <div style={{ padding: 10 }}>
          <div className="row g-2 mb-2 align-items-end">
            <div className="col-md-5">
              <label style={{ fontSize: 10, fontWeight: 700 }}>Item</label>
              <select
                className="form-select form-select-sm"
                style={{ height: 30, fontSize: 11 }}
                value={itemId}
                onChange={(e) => {
                  setItemId(e.target.value);
                  setRows([]);
                }}
              >
                <option value="">Select Item</option>
                {items.map((item) => (
                  <option
                    key={get(item, "Id", "id")}
                    value={get(item, "Id", "id")}
                  >
                    {get(item, "ItemCode", "itemCode")} -{" "}
                    {get(item, "ItemName", "itemName")}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-2 d-grid">
              <button
                className="btn btn-success btn-sm"
                style={{ height: 30, fontSize: 11 }}
                onClick={() => load()}
                disabled={loading}
              >
                <i className="bi bi-search me-1"></i>
                {loading ? "Loading..." : "Load"}
              </button>
            </div>
          </div>

          <div
            className="mb-2"
            style={{
              fontSize: 11,
              padding: "7px 10px",
              borderRadius: 10,
              background: "#f8f9fa",
              border: "1px solid #e9ecef",
            }}
          >
            <strong>Item:</strong>{" "}
            {selectedItem
              ? `${get(selectedItem, "ItemCode", "itemCode")} - ${get(
                  selectedItem,
                  "ItemName",
                  "itemName",
                )}`
              : "-"}
            {" | "}
            <strong>HSN:</strong>{" "}
            {get(selectedItem, "HSNCode", "hsnCode") || "-"}
            {" | "}
            <strong>GST:</strong>{" "}
            {get(selectedItem, "GSTPercent", "gstPercent") || 0}%
          </div>

          <div
            style={{
              overflowX: "auto",
              maxHeight: 430,
              border: "1px solid #e9ecef",
              borderRadius: 10,
            }}
          >
            <table
              className="table table-bordered table-hover align-middle"
              style={{ fontSize: 11, minWidth: 900, marginBottom: 0 }}
            >
              <thead>
                <tr>
                  {[
                    "Date",
                    "Type",
                    "Qty In",
                    "Qty Out",
                    "Rate",
                    "Ref No",
                    "Remarks",
                    "Running Qty",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        fontSize: 10,
                        padding: "7px 8px",
                        color: "#fff",
                        background: "#07111f",
                        whiteSpace: "nowrap",
                        position: "sticky",
                        top: 0,
                      }}
                      className={
                        ["Qty In", "Qty Out", "Rate", "Running Qty"].includes(h)
                          ? "text-end"
                          : ""
                      }
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted p-3">
                      {loading ? "Loading..." : "No stock ledger entries found"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => (
                    <tr key={i}>
                      <td>
                        {String(get(r, "EntryDate", "entryDate") || "").slice(
                          0,
                          10,
                        )}
                      </td>
                      <td>{get(r, "VoucherType", "voucherType") || "-"}</td>
                      <td className="text-end">
                        {num(get(r, "QtyIn", "qtyIn"))}
                      </td>
                      <td className="text-end">
                        {num(get(r, "QtyOut", "qtyOut"))}
                      </td>
                      <td className="text-end">
                        {num(get(r, "Rate", "rate"))}
                      </td>
                      <td>{get(r, "ReferenceNo", "referenceNo") || "-"}</td>
                      <td>{get(r, "Remarks", "remarks") || "-"}</td>
                      <td className="text-end fw-bold text-primary">
                        {num(get(r, "RunningQty", "runningQty"))}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
